import { useMemo } from "react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useWorkspaceAddons } from "@/hooks/useWorkspaceAddons";
import { usePendingArchives } from "@/hooks/usePendingArchives";

/**
 * Detects when the user's owned-workspace count exceeds the new allowed
 * count after an add-on cancellation. Returns the shortfall (number of
 * workspaces they need to nominate for archive) and the cutoff date.
 *
 * Shortfall is reduced by any already-enqueued pending archives so the
 * banner disappears once the user has triaged the right number of
 * workspaces.
 */
export function useAddonShortfall() {
  const { workspaces, workspaceLimit, superAdminBypassCaps } = useWorkspace();
  const { addons } = useWorkspaceAddons();
  const { archives } = usePendingArchives();

  return useMemo(() => {
    if (superAdminBypassCaps) return { shortfall: 0, archiveAt: null as string | null };
    if (workspaceLimit === null) return { shortfall: 0, archiveAt: null as string | null };

    // Count workspaces the user owns (the only ones that count against quota).
    const ownedCount = workspaces.length;
    const overQuota = Math.max(0, ownedCount - workspaceLimit);

    // Already-enqueued archives reduce the prompt — they'll bring count
    // back under quota at archive time.
    const enqueued = archives.filter((a) => a.reason === "addon_removed").length;
    const shortfall = Math.max(0, overQuota - enqueued);

    // Find the soonest-cancelling add-on to use as the archive cutoff.
    const cancellingAddons = addons
      .filter((a) => a.status === "canceled" && a.current_period_end)
      .sort((a, b) => new Date(a.current_period_end!).getTime() - new Date(b.current_period_end!).getTime());

    const archiveAt = cancellingAddons[0]?.current_period_end ?? null;

    return { shortfall, archiveAt };
  }, [workspaces, workspaceLimit, addons, archives, superAdminBypassCaps]);
}
